import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import client, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ShieldAlert, Search, Loader2, Trash2, Lock, FlaskConical, AlertTriangle, CheckCircle2, RefreshCw,
  Download, FileText, FileSpreadsheet, History, CheckCircle, X,
} from "lucide-react";
import { toast } from "sonner";

const CONFIRM_PHRASE = "DELETE TEST DATA";

const OP_LABELS = {
  jobs: "Jobs", attendance: "Attendance", jobEvents: "Job time", schedules: "Shifts",
  helpers: "Helpers", overtime: "Overtime", shortHours: "Short hours",
  forms: "Forms", issues: "Issues", messages: "Messages", leave: "Leave",
};

const RESULT_LABELS = {
  jobs: "Jobs", attendance: "Attendance records", jobEvents: "Job-time records",
  schedules: "Schedules", helpers: "Helper records", overtime: "Overtime records",
  shortHours: "Short-hour records", leave: "Leave requests", messages: "Messages",
  issues: "Issues", forms: "Forms", checklists: "Checklist records", documents: "Documents",
  siteAssignments: "Site assignments", supervisorLinks: "Supervisor links",
  privateNotes: "Private notes", permissionOverrides: "Permission overrides",
  notificationReads: "Notification reads", messageReads: "Message reads",
  availability: "Availability blocks", credentials: "Credentials",
};

function StatusChip({ c }) {
  const map = {
    protected: { cls: "bg-red-50 text-red-700 border-red-200", icon: Lock, label: "Protected" },
    needs_review: { cls: "bg-amber-50 text-amber-700 border-amber-200", icon: AlertTriangle, label: "Needs review" },
    test: { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: FlaskConical, label: "Likely test" },
    clean: { cls: "bg-slate-100 text-slate-600 border-slate-200", icon: CheckCircle2, label: "No operational data" },
  };
  const m = map[c] || map.clean;
  const Icon = m.icon;
  return (
    <Badge variant="outline" className={`gap-1 ${m.cls}`} data-testid={`status-chip-${c}`}>
      <Icon className="h-3 w-3" /> {m.label}
    </Badge>
  );
}

function OpBreakdown({ op }) {
  const entries = Object.entries(op).filter(([, v]) => v > 0);
  if (!entries.length) return <span className="text-xs text-slate-400">None</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {entries.map(([k, v]) => (
        <span key={k} className="text-[11px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
          {OP_LABELS[k] || k}: {v}
        </span>
      ))}
    </div>
  );
}

export default function TestDataCleanup() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [selected, setSelected] = useState({});
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await client.get("/admin/test-cleanup-history");
      setHistory(data.cleanups || []);
    } catch (e) { /* non-blocking */ }
  }, []);

  const download = async (cleanupId, format) => {
    try {
      const res = await client.get(`/admin/test-cleanup-report/${cleanupId}`, {
        params: { format }, responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `iclean_test_cleanup_${String(cleanupId).split("-")[0]}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed. Please try again.");
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await client.get("/admin/test-cleanup-candidates");
      setRows(data.candidates || []);
    } catch (e) {
      toast.error(apiErr(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user && user.role !== "owner") navigate("/", { replace: true });
  }, [user, navigate]);

  useEffect(() => { load(); loadHistory(); }, [load, loadHistory]);

  const selectable = (r) => !r.protected;

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (!showAll && r.classification === "clean" && !q) return false;
      if (!showAll && r.protected && !q) return false;
      if (q) return `${r.email} ${r.displayName}`.toLowerCase().includes(q);
      return true;
    });
  }, [rows, search, showAll]);

  const selectedIds = useMemo(() => Object.keys(selected).filter((k) => selected[k]), [selected]);
  const selectedRows = useMemo(() => rows.filter((r) => selected[r.id] && selectable(r)), [rows, selected]);
  const canDelete = selectedIds.length > 0 && confirm === CONFIRM_PHRASE && !busy;

  const toggle = (r) => {
    if (!selectable(r)) return;
    setSelected((s) => ({ ...s, [r.id]: !s[r.id] }));
  };

  const execute = async () => {
    if (!canDelete) return;
    setBusy(true);
    try {
      const { data } = await client.post("/admin/test-cleanup-execute", {
        userIds: selectedIds,
        confirm,
      });
      const preservedCount = data.recordsPreserved || 0;
      toast.success(`Permanently deleted ${data.deletedCount} test account(s).${preservedCount ? ` ${preservedCount} shared record(s) preserved.` : ""}`);
      setLastResult(data);
      setSelected({});
      setConfirm("");
      await load();
      await loadHistory();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.message) toast.error(detail.message);
      else toast.error(apiErr(detail));
    } finally {
      setBusy(false);
    }
  };

  const counts = useMemo(() => {
    const c = { test: 0, needs_review: 0, protected: 0, clean: 0 };
    rows.forEach((r) => { c[r.classification] = (c[r.classification] || 0) + 1; });
    return c;
  }, [rows]);

  return (
    <div className="space-y-6" data-testid="test-cleanup-page">
      <div className="flex items-start gap-3">
        <div className="h-11 w-11 rounded-xl bg-red-50 border border-red-200 flex items-center justify-center flex-shrink-0">
          <ShieldAlert className="h-6 w-6 text-red-600" />
        </div>
        <div>
          <h2 className="font-display text-2xl font-bold text-slate-900">Test Data Cleanup</h2>
          <p className="text-sm text-slate-500 max-w-2xl">
            Owner-only tool to permanently remove development/test accounts and their associated test data.
            Detection patterns are only suggestions — you must hand-pick each account. As the Owner, your
            explicit selection plus the typed confirmation authorises a permanent purge, <span className="font-semibold text-slate-600">including any
            jobs, attendance and other operational history</span> that belongs to the test account. Seeded/system
            accounts and your own account stay protected. Shared records involving genuine staff are preserved.
          </p>
        </div>
      </div>

      {lastResult && (
        <Card className="p-5 border-emerald-300 bg-emerald-50/40" data-testid="cleanup-result-panel">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-emerald-600" />
              <h3 className="font-display text-lg font-bold text-emerald-800">Test Data Cleanup Complete</h3>
            </div>
            <button data-testid="result-dismiss" onClick={() => setLastResult(null)} className="text-slate-400 hover:text-slate-600">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <div><p className="text-[11px] uppercase text-slate-500">Cleanup date/time</p><p className="text-sm font-medium text-slate-800" data-testid="result-at">{new Date(lastResult.at).toLocaleString()}</p></div>
            <div><p className="text-[11px] uppercase text-slate-500">Accounts deleted</p><p className="text-sm font-bold text-slate-800" data-testid="result-accounts">{lastResult.deletedCount}</p></div>
            <div><p className="text-[11px] uppercase text-slate-500">Records deleted</p><p className="text-sm font-bold text-red-700" data-testid="result-deleted">{lastResult.recordsDeleted}</p></div>
            <div><p className="text-[11px] uppercase text-slate-500">Records preserved</p><p className="text-sm font-bold text-emerald-700" data-testid="result-preserved">{lastResult.recordsPreserved}</p></div>
          </div>

          <div className="space-y-3">
            {(lastResult.accounts || []).map((a) => (
              <div key={a.id} className="rounded-lg border border-emerald-200 bg-white p-3" data-testid={`result-account-${a.id}`}>
                <p className="text-sm font-semibold text-slate-800">{a.displayName || "—"} <span className="font-normal text-slate-400">· {a.email}</span></p>
                <p className="text-[11px] text-slate-400 mb-2">{a.role} · {a.workerType} · {a.status}</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <p className="text-[11px] font-semibold uppercase text-red-600 mb-1">Deleted</p>
                    {Object.entries(a.deletedRecords || {}).length ? (
                      <ul className="text-xs text-slate-600 space-y-0.5">
                        {Object.entries(a.deletedRecords).map(([k, v]) => (
                          <li key={k}>- {v} {RESULT_LABELS[k] || k}</li>
                        ))}
                      </ul>
                    ) : <p className="text-xs text-slate-400">- No records</p>}
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold uppercase text-emerald-600 mb-1">Preserved</p>
                    {(a.preserved || []).length ? (
                      <ul className="text-xs text-slate-600 space-y-0.5">
                        {a.preserved.map((p, i) => (
                          <li key={i}>- {p.count ? `${p.count} ` : ""}{p.type}{p.id ? ` (${p.id.slice(-6)})` : ""} — {p.reason}</li>
                        ))}
                      </ul>
                    ) : <p className="text-xs text-slate-400">- None</p>}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap gap-2 mt-4">
            <Button data-testid="result-download-csv" variant="outline" size="sm" onClick={() => download(lastResult.cleanupId, "csv")}>
              <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Download CSV
            </Button>
            <Button data-testid="result-download-pdf" variant="outline" size="sm" onClick={() => download(lastResult.cleanupId, "pdf")}>
              <FileText className="h-4 w-4 mr-1.5" /> Download PDF
            </Button>
          </div>
        </Card>
      )}

      <div className="flex flex-wrap gap-3">
        <Card className="px-4 py-3 border-emerald-200 bg-emerald-50/40"><p className="text-xs text-emerald-700">Likely test</p><p className="text-xl font-bold text-emerald-700" data-testid="count-test">{counts.test}</p></Card>
        <Card className="px-4 py-3 border-amber-200 bg-amber-50/40"><p className="text-xs text-amber-700">Needs review</p><p className="text-xl font-bold text-amber-700" data-testid="count-review">{counts.needs_review}</p></Card>
        <Card className="px-4 py-3 border-red-200 bg-red-50/40"><p className="text-xs text-red-700">Protected</p><p className="text-xl font-bold text-red-700" data-testid="count-protected">{counts.protected}</p></Card>
      </div>

      <Card className="p-4 border-slate-200">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input data-testid="cleanup-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or email…" className="pl-9" />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer" data-testid="show-all-toggle">
            <Checkbox checked={showAll} onCheckedChange={(v) => setShowAll(!!v)} />
            Show all users
          </label>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="cleanup-refresh">
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Rescan
          </Button>
        </div>

        {loading ? (
          <div className="py-16 flex items-center justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin mr-2" /> Scanning…</div>
        ) : visible.length === 0 ? (
          <div className="py-16 text-center text-slate-400 text-sm" data-testid="cleanup-empty">
            No accounts to show. Toggle “Show all users” to review every account.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-200">
                  <th className="py-2 pr-2 w-8"></th>
                  <th className="py-2 pr-4">Account</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4">Operational data</th>
                  <th className="py-2 pr-4">Account-scoped</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((r) => (
                  <tr key={r.id} data-testid={`cleanup-row-${r.id}`}
                      className={`border-b border-slate-100 ${selected[r.id] ? "bg-red-50/50" : ""}`}>
                    <td className="py-3 pr-2 align-top">
                      <Checkbox
                        data-testid={`cleanup-select-${r.id}`}
                        disabled={!selectable(r)}
                        checked={!!selected[r.id]}
                        onCheckedChange={() => toggle(r)}
                      />
                    </td>
                    <td className="py-3 pr-4 align-top">
                      <p className="font-semibold text-slate-800">{r.displayName || "—"}</p>
                      <p className="text-xs text-slate-500">{r.email}</p>
                      <p className="text-[11px] text-slate-400 mt-0.5">{r.role}{r.isTest && !r.protected ? " · matches test pattern" : ""}</p>
                    </td>
                    <td className="py-3 pr-4 align-top"><StatusChip c={r.classification} /></td>
                    <td className="py-3 pr-4 align-top max-w-[280px]"><OpBreakdown op={r.operational} /></td>
                    <td className="py-3 pr-4 align-top text-xs text-slate-500">{r.ancillaryTotal} record(s)</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card className="p-5 border-red-200 bg-red-50/30" data-testid="cleanup-danger-zone">
        <div className="flex items-center gap-2 mb-2">
          <Trash2 className="h-4 w-4 text-red-600" />
          <h3 className="font-semibold text-red-700">Permanent deletion</h3>
        </div>

        {selectedRows.length > 0 && (
          <div className="mb-4 space-y-2" data-testid="cleanup-impact">
            <p className="text-sm font-medium text-slate-700">Deletion impact — {selectedRows.length} account(s) selected:</p>
            {selectedRows.map((r) => {
              const ops = Object.entries(r.operational).filter(([, v]) => v > 0);
              return (
                <div key={r.id} className="rounded-lg border border-red-200 bg-white p-3" data-testid={`impact-${r.id}`}>
                  <p className="text-sm font-semibold text-slate-800">{r.displayName || r.email} <span className="font-normal text-slate-400">· {r.email}</span></p>
                  {ops.length ? (
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {ops.map(([k, v]) => (
                        <span key={k} className="text-[11px] px-1.5 py-0.5 rounded bg-red-50 text-red-700 border border-red-200">{OP_LABELS[k] || k}: {v}</span>
                      ))}
                      <span className="text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 border border-slate-200">Account-scoped: {r.ancillaryTotal}</span>
                    </div>
                  ) : (
                    <p className="text-xs text-slate-400 mt-0.5">No operational data · {r.ancillaryTotal} account-scoped record(s)</p>
                  )}
                </div>
              );
            })}
            <p className="text-xs text-red-700 font-medium">
              These records will be permanently deleted as part of this test-data cleanup. Shared records
              involving genuine staff (e.g. their messages, multi-worker jobs) are preserved automatically.
            </p>
          </div>
        )}

        <p className="text-sm text-slate-600 mb-4">
          This is irreversible. Type
          <span className="font-mono font-bold text-red-700"> {CONFIRM_PHRASE} </span>
          below to enable deletion. The server independently re-verifies every account and always refuses
          protected/system accounts and your own account.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <Input
            data-testid="cleanup-confirm-input"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder={CONFIRM_PHRASE}
            className="max-w-xs font-mono"
          />
          <Button
            data-testid="cleanup-execute-btn"
            onClick={execute}
            disabled={!canDelete}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1" />}
            Delete test data
          </Button>
        </div>
      </Card>

      <Card className="p-4 border-slate-200" data-testid="cleanup-history-card">
        <div className="flex items-center gap-2 mb-3">
          <History className="h-4 w-4 text-slate-500" />
          <h3 className="font-semibold text-slate-800">Previous cleanups</h3>
        </div>
        {history.length === 0 ? (
          <p className="text-sm text-slate-400" data-testid="history-empty">
            No downloadable cleanup reports yet. Detailed reports are available for cleanups performed from now on.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-400 border-b border-slate-200">
                  <th className="py-2 pr-4">Date/time</th>
                  <th className="py-2 pr-4">Owner</th>
                  <th className="py-2 pr-4">Accounts</th>
                  <th className="py-2 pr-4">Deleted</th>
                  <th className="py-2 pr-4">Preserved</th>
                  <th className="py-2 pr-4">Report</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.cleanupId} className="border-b border-slate-100" data-testid={`history-row-${h.cleanupId}`}>
                    <td className="py-2.5 pr-4 text-slate-700">{new Date(h.at).toLocaleString()}</td>
                    <td className="py-2.5 pr-4 text-slate-600">{h.owner}</td>
                    <td className="py-2.5 pr-4 text-slate-600">{h.deletedCount}</td>
                    <td className="py-2.5 pr-4 text-red-700 font-medium">{h.recordsDeleted}</td>
                    <td className="py-2.5 pr-4 text-emerald-700 font-medium">{h.recordsPreserved}</td>
                    <td className="py-2.5 pr-4">
                      <div className="flex gap-2">
                        <button data-testid={`history-csv-${h.cleanupId}`} onClick={() => download(h.cleanupId, "csv")} className="text-slate-500 hover:text-brand inline-flex items-center gap-1 text-xs"><FileSpreadsheet className="h-3.5 w-3.5" /> CSV</button>
                        <button data-testid={`history-pdf-${h.cleanupId}`} onClick={() => download(h.cleanupId, "pdf")} className="text-slate-500 hover:text-brand inline-flex items-center gap-1 text-xs"><FileText className="h-3.5 w-3.5" /> PDF</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
